// DisciplinasView (teacher) — minhas disciplinas com offerings + assessments.
// Lista é uma view; o detalhe ANTES era um modal denso, agora também é uma
// view com breadcrumb (Disciplinas › Nome da disciplina), porque tem muito
// conteúdo (avaliação composta, notas, turmas, provas, conteúdos, mensagens,
// pacotes) — modal espremia tudo num scroll de 92vh.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  CheckCircle2,
  Clock,
  Hourglass,
  Library,
  Loader2,
  Plus,
  Search,
  Users
} from "lucide-react";
import { api, ApiError, type DisciplinaDetail, type DisciplinaRow } from "../api";
import { subCrumbs, viewingDisciplinaId } from "../App";
import { PacotesSection } from "./PacotesSection";
import { MensagensSection } from "./MensagensSection";
import { ConteudosSection } from "./ConteudosSection";
import { AvaliacaoSection } from "./AvaliacaoSection";
import { friendlyError } from "@app/ui/lib/friendly-error";

const disciplinas = signal<DisciplinaRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const opening = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listDisciplinas();
    disciplinas.value = r.disciplinas;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

async function openDetail(id: string) {
  viewingDisciplinaId.value = id;
}

export function DisciplinasView() {
  // Quando há disciplinaId selecionada, renderiza a view de detalhe em vez
  // da lista (sem modal). O detalhe gerencia os subCrumbs sozinho.
  if (viewingDisciplinaId.value) {
    return <DisciplinaDetailView id={viewingDisciplinaId.value} />;
  }

  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  const rows = disciplinas.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((d) => {
      if (q) {
        const hay = `${d.name} ${d.code ?? ""} ${d.description ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q]);

  return (
    <div class="space-y-4">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Library className="size-6 text-primary" />
          Disciplinas
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          As disciplinas em que você é professor. A organização define nome/código/turmas;
          você cria provas, atribui bancos e gerencia conteúdo dentro.
        </p>
      </header>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar disciplina…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Você não está vinculado a nenhuma disciplina ainda. Peça pro admin da
          organização te associar a uma.
        </div>
      ) : (
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((d) => (
            <DisciplinaCard key={d.id} d={d} />
          ))}
        </div>
      )}

    </div>
  );
}

function DisciplinaCard({ d }: { d: DisciplinaRow }) {
  const isOpening = opening.value === d.id;
  return (
    <button
      type="button"
      onClick={() => openDetail(d.id)}
      disabled={isOpening}
      class="group flex flex-col items-start gap-2 rounded-md border border-border bg-card/40 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md disabled:opacity-50"
    >
      <div class="flex w-full items-start justify-between gap-2">
        <Library width="1.3em" height="1.3em" className="text-violet-600 dark:text-violet-300" />
        <StatusBadge status={d.status} />
      </div>
      <div class="min-w-0 flex-1">
        <h3 class="truncate text-sm font-bold">{d.name}</h3>
        {d.code && <p class="font-mono text-[10px] text-muted-foreground">{d.code}</p>}
      </div>
      <div class="flex items-center gap-2 text-[11px]">
        <span class="inline-flex items-center gap-1 rounded bg-accent/60 px-1.5 py-0.5 font-bold tabular-nums">
          <Users width="0.9em" height="0.9em" /> {d.offeringCount}
        </span>
        {d.gradingEnabled && (
          <span class="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            com nota
          </span>
        )}
      </div>
      {isOpening && (
        <span class="text-[10px] text-muted-foreground">
          <Loader2 className="inline size-3 animate-spin" /> abrindo…
        </span>
      )}
    </button>
  );
}

function StatusBadge({ status }: { status: "draft" | "active" | "closed" }) {
  if (status === "active") {
    return (
      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 width="0.85em" height="0.85em" /> Ativa
      </span>
    );
  }
  if (status === "closed") {
    return (
      <span class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Encerrada
      </span>
    );
  }
  return (
    <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
      <Hourglass width="0.85em" height="0.85em" /> Rascunho
    </span>
  );
}

// Detalhe da disciplina — agora uma view (não modal). Carrega os dados via
// api.getDisciplina, mantém os subCrumbs em sync (entry/exit), e renderiza
// em largura inteira do layout (sem o aperto de modal max-w-3xl).
function DisciplinaDetailView({ id }: { id: string }) {
  const [data, setData] = useState<DisciplinaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  function back() {
    viewingDisciplinaId.value = null;
    subCrumbs.value = [];
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        const r = await api.getDisciplina(id);
        if (cancelled) return;
        setData(r.disciplina);
        subCrumbs.value = [{ label: r.disciplina.name }];
      } catch (e) {
        if (cancelled) return;
        setErr(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Limpa os crumbs ao desmontar (volta pra raiz Disciplinas).
      subCrumbs.value = [];
    };
  }, [id]);

  if (loading) {
    return (
      <div class="space-y-4">
        <BackBar onBack={back} />
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando disciplina…
        </p>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div class="space-y-4">
        <BackBar onBack={back} />
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <AlertCircle className="mr-2 inline size-4 text-destructive" />{" "}
          {err ?? "Disciplina não encontrada."}
        </div>
      </div>
    );
  }

  const provas = data.assessments.filter((a) => a.kind === "prova");
  const trabalhos = data.assessments.filter((a) => a.kind === "trabalho");

  return (
    <div class="space-y-5">
      <BackBar onBack={back} />

      {/* Header da disciplina */}
      <header class="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card/40 p-4">
        <div class="flex min-w-0 items-start gap-3">
          <span class="grid size-12 shrink-0 place-items-center rounded-md bg-violet-500/15 text-violet-600 ring-1 ring-violet-500/30 dark:text-violet-300">
            <Library className="size-6" />
          </span>
          <div class="min-w-0">
            <h1 class="text-xl font-bold tracking-tight">{data.name}</h1>
            {data.code && (
              <p class="font-mono text-[11px] text-muted-foreground">{data.code}</p>
            )}
            {data.description && (
              <p class="mt-1 text-sm text-muted-foreground">{data.description}</p>
            )}
          </div>
        </div>
        <div class="flex items-center gap-2">
          <StatusBadge status={data.status} />
          {data.gradingEnabled && (
            <span class="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              Com nota
            </span>
          )}
        </div>
      </header>

      {/* Grid 2 colunas no desktop: esquerda = avaliação + pacotes;
          direita = turmas + provas + trabalhos. Conteúdos e mensagens
          ficam abaixo full-width porque tendem a ter listas longas. */}
      <div class="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <div class="space-y-5">
          <AvaliacaoSection disciplinaId={data.id} />
          <PacotesSection disciplinaId={data.id} />
        </div>
        <div class="space-y-5">
          <section class="rounded-md border border-border bg-card/40 p-4">
            <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <Users width="1em" height="1em" /> Turmas ({data.offerings.length})
            </h3>
            {data.offerings.length === 0 ? (
              <p class="text-xs italic text-muted-foreground">
                Nenhuma turma associada. Peça pro admin da organização vincular.
              </p>
            ) : (
              <ul class="space-y-1">
                {data.offerings.map((o) => (
                  <li
                    key={o.id}
                    class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-1.5 text-xs"
                  >
                    <span class="flex-1 truncate font-semibold">{o.turmaName}</span>
                    {o.turmaCode && (
                      <span class="font-mono text-[10px] text-muted-foreground">
                        {o.turmaCode}
                      </span>
                    )}
                    <span class="text-[10px] text-muted-foreground">
                      {data.students.filter((s) => s.turmaId === o.turmaId).length} alunos
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section class="rounded-md border border-border bg-card/40 p-4">
            <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <Clock width="1em" height="1em" /> Provas ({provas.length})
            </h3>
            {provas.length === 0 ? (
              <p class="text-xs italic text-muted-foreground">
                Sem provas. Vá em "Provas agendadas" no menu pra criar.
              </p>
            ) : (
              <ul class="space-y-1">
                {provas.map((p) => (
                  <li
                    key={p.id}
                    class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-1.5 text-xs"
                  >
                    <span class="flex-1 truncate font-semibold">{p.title}</span>
                    {p.turmaName && (
                      <span class="text-[10px] text-muted-foreground">{p.turmaName}</span>
                    )}
                    {p.startsAt && (
                      <span class="font-mono text-[10px] text-muted-foreground">
                        {new Date(p.startsAt).toLocaleDateString("pt-BR")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.gradingEnabled && trabalhos.length > 0 && (
            <section class="rounded-md border border-border bg-card/40 p-4">
              <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <CheckCircle width="1em" height="1em" /> Trabalhos ({trabalhos.length})
              </h3>
              <ul class="space-y-1">
                {trabalhos.map((p) => (
                  <li
                    key={p.id}
                    class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-1.5 text-xs"
                  >
                    <span class="flex-1 truncate font-semibold">{p.title}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <ConteudosSection
        offerings={data.offerings.map((o) => ({
          id: o.id,
          turmaName: o.turmaName,
          turmaCode: o.turmaCode ?? null
        }))}
      />

      <MensagensSection
        offerings={data.offerings.map((o) => ({
          id: o.id,
          turmaName: o.turmaName,
          turmaCode: o.turmaCode ?? null
        }))}
      />

      <p class="text-[10px] text-muted-foreground/70">
        Para criar uma nova prova vinculada a esta disciplina, vá em{" "}
        <b>Provas agendadas</b> no menu lateral e selecione esta turma.
      </p>
    </div>
  );
}

function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
    >
      <ArrowLeft className="size-3.5" />
      Voltar para Disciplinas
    </button>
  );
}
